import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI, resolveSwagImage, SWAG_IMAGE_PLACEHOLDER } from '../config/apiService';

// SIP admin inventory manager.
// - Lists every SIPSwagItemInventory row (item x size) and groups by item.
// - Per row: edit RemainingQuantity inline; Ordered is order-driven (read-only).
// - Search filters by item description.
// - "Add new item" is stubbed until image storage is decided.
function InventoryManager() {
    const [rows, setRows] = useState([]);
    const [edits, setEdits] = useState({});      // sipSwagItemInventoryID -> string|number (remaining)
    const [binEdits, setBinEdits] = useState({}); // sipSwagItemInventoryID -> bin string
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgKind, setMsgKind] = useState('info');

    const flash = (text, kind = 'info') => { setMsg(text); setMsgKind(kind); };

    const reload = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getInventory();
            setRows(Array.isArray(res.data) ? res.data : []);
            setEdits({});
            setBinEdits({});
        } catch (err) {
            flash('Failed to load inventory.', 'error');
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    // Refetch rows WITHOUT flipping the global `loading` flag (which would unmount
    // the Add picker mid-action and make it flash). Preserves any pending edits.
    const refreshRowsQuiet = async () => {
        try { const res = await adminAPI.getInventory(); setRows(Array.isArray(res.data) ? res.data : []); }
        catch { /* leave existing rows in place */ }
    };

    const grouped = useMemo(() => {
        const lower = search.trim().toLowerCase();
        const filtered = lower
            ? rows.filter((r) => (r.itemDescription || '').toLowerCase().includes(lower))
            : rows;
        const map = new Map();
        for (const r of filtered) {
            if (!map.has(r.swagItemID)) {
                map.set(r.swagItemID, {
                    swagItemID: r.swagItemID,
                    itemDescription: r.itemDescription,
                    pointGroup: r.pointGroup,
                    imagePath: r.imagePath,
                    sizes: []
                });
            }
            map.get(r.swagItemID).sizes.push(r);
        }
        return [...map.values()];
    }, [rows, search]);

    const handleEdit = (id, value) => {
        const v = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
        setEdits((prev) => ({ ...prev, [id]: v }));
    };

    // Bin: alphanumeric, upper-cased, max 3 chars.
    const cleanBin = (value) => (value || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 3).toUpperCase();
    const handleBinEdit = (id, value) => {
        setBinEdits((prev) => ({ ...prev, [id]: cleanBin(value) }));
    };
    // "Set all" — fill every size in an item with one bin.
    const setGroupBin = (item, value) => {
        const v = cleanBin(value);
        setBinEdits((prev) => {
            const next = { ...prev };
            for (const s of item.sizes) next[s.sipSwagItemInventoryID] = v;
            return next;
        });
    };

    const changedEntries = () => Object.entries(edits)
        .map(([id, v]) => ({ id: Number(id), v }))
        .filter(({ id, v }) => {
            if (v === '') return false;
            const cur = rows.find((r) => r.sipSwagItemInventoryID === id);
            return cur && Number(v) !== (cur.remainingQuantity ?? 0);
        });

    const changedBinEntries = () => Object.entries(binEdits)
        .map(([id, v]) => ({ id: Number(id), v: v ?? '' }))
        .filter(({ id, v }) => {
            const cur = rows.find((r) => r.sipSwagItemInventoryID === id);
            return cur && v !== (cur.bin ?? '');
        });

    const editedCount = changedEntries().length + changedBinEntries().length;

    const saveAll = async () => {
        const counts = changedEntries();
        const bins = changedBinEntries();
        if (counts.length === 0 && bins.length === 0) { flash('No changes to save.', 'info'); return; }
        setBusy(true); setMsg('');
        let ok = 0, fail = 0, firstErr = '';
        const grab = (e) => { if (!firstErr) firstErr = e?.response?.data || e?.message || 'unknown error'; };
        for (const { id, v } of counts) {
            try { await adminAPI.updateInventoryCount(id, Number(v)); ok++; } catch (e) { fail++; grab(e); }
        }
        for (const { id, v } of bins) {
            try { await adminAPI.setInventoryBin(id, v); ok++; } catch (e) { fail++; grab(e); }
        }
        await reload();
        if (fail === 0) flash(`Saved ${ok} change${ok === 1 ? '' : 's'}.`, 'success');
        else flash(`Saved ${ok}, ${fail} failed. First error: ${firstErr}`, 'error');
        setBusy(false);
    };

    // "Add existing item to inventory" picker: pull a catalog SwagItem into
    // SIPSwagItemInventory (idempotent — only creates missing size rows).
    const [showPicker, setShowPicker] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [candSearch, setCandSearch] = useState('');
    const [candBusy, setCandBusy] = useState(false);

    const openPicker = async () => {
        setShowPicker(true); setCandBusy(true);
        try { const r = await adminAPI.getInventoryCandidates(); setCandidates(r.data || []); }
        catch (e) { flash(`Could not load items: ${e?.response?.data || e?.message}`, 'error'); }
        finally { setCandBusy(false); }
    };
    const addToInv = async (c) => {
        // An item with no active sizes has nothing to create inventory rows for —
        // the add would be a silent no-op. Tell the user where to set sizes.
        if (!c.sizeCount) {
            flash(`"${c.swagItem}" has no sizes yet — set its sizes in Settings → Swag Items, then add it here.`, 'error');
            return;
        }
        setCandBusy(true);
        try {
            const r = await adminAPI.addItemToInventory(c.swagItemID);
            const n = r.data?.sizesAdded ?? 0;
            flash(n > 0 ? `Added "${c.swagItem}" — ${n} size${n === 1 ? '' : 's'} now in inventory.` : `"${c.swagItem}" is already in inventory.`, n > 0 ? 'success' : 'info');
            await refreshRowsQuiet();  // update the grid behind the picker, no flash
            const cr = await adminAPI.getInventoryCandidates(); setCandidates(cr.data || []);
        } catch (e) { flash(`Add failed: ${e?.response?.data || e?.message}`, 'error'); }
        finally { setCandBusy(false); }
    };

    const AddPicker = () => {
        if (!showPicker) return null;
        const lower = candSearch.trim().toLowerCase();
        const list = candidates
            .filter((c) => !lower || (c.swagItem || '').toLowerCase().includes(lower) || (c.orderFormDescription || '').toLowerCase().includes(lower));
        return (
            <div className="invpick-overlay" onClick={() => setShowPicker(false)}>
                <style>{PICKER_CSS}</style>
                <div className="invpick-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="invpick-head">
                        <div className="invpick-title">Add existing item to inventory</div>
                        <button className="invpick-close" onClick={() => setShowPicker(false)}>Close</button>
                    </div>
                    <p className="invpick-note">Pulls a catalog item into SIP inventory at qty 0 (only sizes not already here). To create a brand-new catalog item, use <strong>Settings → Swag Items</strong>.</p>
                    {msg && (
                        <div className="invpick-msg" style={{ background: msgKind === 'error' ? '#fef2f2' : msgKind === 'success' ? '#f0fdf4' : '#eff6ff', color: msgKind === 'error' ? '#b91c1c' : msgKind === 'success' ? '#15803d' : '#1d4ed8', border: `1px solid ${msgKind === 'error' ? '#fecaca' : msgKind === 'success' ? '#bbf7d0' : '#bfdbfe'}` }}>{msg}</div>
                    )}
                    <input className="invpick-search" placeholder="search catalog…" value={candSearch} onChange={(e) => setCandSearch(e.target.value)} />
                    <div className="invpick-list">
                        {candBusy && candidates.length === 0 ? <div className="invpick-muted">Loading…</div> :
                         list.map((c) => {
                            const noSizes = !c.sizeCount;
                            const full = c.sizeCount > 0 && c.sizesInInventory >= c.sizeCount;
                            return (
                                <div key={c.swagItemID} className="invpick-row">
                                    <img className="invpick-img" src={resolveSwagImage(c.imageName)} alt="" onError={(e) => { e.currentTarget.src = SWAG_IMAGE_PLACEHOLDER; }} />
                                    <div className="invpick-text">
                                        <div className="invpick-name">{c.swagItem}</div>
                                        <div className="invpick-sub">
                                            {noSizes
                                                ? <span style={{ color: '#b45309' }}>{c.selectionGroup} · {c.pointGroup} pts · no sizes set</span>
                                                : <>{c.selectionGroup} · {c.pointGroup} pts · {c.sizesInInventory}/{c.sizeCount} in inventory</>}
                                        </div>
                                    </div>
                                    <button className="invpick-add" onClick={() => addToInv(c)} disabled={candBusy || full || noSizes} title={noSizes ? 'Set sizes in Settings → Swag Items first' : undefined}>
                                        {noSizes ? 'No sizes' : full ? 'In inventory' : 'Add'}
                                    </button>
                                </div>
                            );
                        })}
                        {!candBusy && list.length === 0 && <div className="invpick-muted">No catalog items match.</div>}
                    </div>
                </div>
            </div>
        );
    };

    if (loading) return <div style={styles.muted}>Loading…</div>;

    if (rows.length === 0) {
        return (
            <div style={styles.card}>
                {msg && <Banner kind={msgKind}>{msg}</Banner>}
                <h2 style={styles.cardTitle}>No SIP inventory rows yet</h2>
                <p style={styles.muted}>Pull existing catalog items into inventory, or add new ones in Settings → Swag Items.</p>
                <button onClick={openPicker} style={btn('#2563eb', false)}>+ Add existing item to inventory</button>
                <AddPicker />
            </div>
        );
    }

    const visibleItemCount = grouped.length;
    const totalItemCount = new Set(rows.map((r) => r.swagItemID)).size;

    return (
        <div>
            <style>{PRINT_CSS}</style>

            <div className="inv-noprint">
                <div style={styles.card}>
                    <div style={styles.headerRow}>
                        <div>
                            <div style={styles.eyebrow}>Inventory</div>
                            <div style={styles.title}>Stock &amp; per-size counts</div>
                            <div style={styles.muted}>
                                {visibleItemCount} of {totalItemCount} item{totalItemCount === 1 ? '' : 's'} shown
                                {' · '}
                                {editedCount} unsaved change{editedCount === 1 ? '' : 's'}
                            </div>
                        </div>
                        <div style={styles.actions}>
                            <button onClick={saveAll} disabled={busy || editedCount === 0} style={btn('#2563eb', busy || editedCount === 0)}>
                                Save changes
                            </button>
                            <button onClick={() => window.print()} disabled={busy} style={btn('#15803d', busy)} title="Print inventory sheet (thumbnail, size, remaining, bin, ordered)">
                                🖨 Print sheet
                            </button>
                            <button onClick={openPicker} disabled={busy} style={btn('#6b7280', busy)} title="Pull an existing catalog item into SIP inventory">
                                + Add existing item
                            </button>
                        </div>
                    </div>
                    {editedCount > 0 && (
                        <div style={{ ...styles.muted, marginTop: 6, color: '#b45309' }}>
                            You have unsaved changes — Save before printing so the sheet matches.
                        </div>
                    )}
                </div>

                <div style={{ ...styles.card, marginTop: 12 }}>
                    <div style={styles.bulkRow}>
                        <div style={styles.bulkLabel}>Search:</div>
                        <input
                            type="text"
                            placeholder="filter by description…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ ...styles.input, minWidth: 220 }}
                        />
                        {search && (
                            <button onClick={() => setSearch('')} style={btn('#9ca3af', false)}>Clear</button>
                        )}
                    </div>
                </div>

                {msg && <Banner kind={msgKind}>{msg}</Banner>}

                {grouped.length === 0 ? (
                    <div style={{ ...styles.card, marginTop: 12 }}>
                        <div style={styles.muted}>No items match that search.</div>
                    </div>
                ) : (
                    <div style={styles.grid}>
                        {grouped.map((item) => (
                            <ItemCard key={item.swagItemID} item={item} edits={edits} binEdits={binEdits} onEdit={handleEdit} onBinEdit={handleBinEdit} onGroupBin={setGroupBin} />
                        ))}
                    </div>
                )}
            </div>

            <InventoryPrintSheet grouped={grouped} edits={edits} binEdits={binEdits} />
            <AddPicker />
        </div>
    );
}

// Print-only table, grouped by item: one large photo (spanning the item's
// sizes, no internal dividers) in the leftmost column, then Item · Size ·
// Remaining · Bin per size. Reflects on-screen edits so it matches the screen.
// Row heights scale so a single-size item is ~3 rows tall, a double ~1.5,
// 3+ stay normal — giving the big photo room in every case. No "Ordered".
function InventoryPrintSheet({ grouped, edits, binEdits }) {
    return (
        <div className="inv-print">
            <h2 style={{ fontSize: 18, margin: '0 0 10px' }}>SIP Inventory Sheet</h2>
            <table>
                <thead>
                    <tr>
                        <th className="imgcol">Photo</th>
                        <th>Item</th>
                        <th>Size</th>
                        <th className="num">Remaining</th>
                        <th>Bin</th>
                    </tr>
                </thead>
                <tbody>
                    {grouped.map((item) => {
                        const n = item.sizes.length;
                        const rowH = Math.max(44, Math.round(132 / n)); // block ≈132px tall
                        return item.sizes.map((s, i) => {
                            const id = s.sipSwagItemInventoryID;
                            const remaining = (id in edits && edits[id] !== '') ? edits[id] : (s.remainingQuantity ?? 0);
                            const bin = (id in binEdits) ? binEdits[id] : (s.bin ?? '');
                            return (
                                <tr key={id} style={{ height: rowH }}>
                                    {i === 0 && (
                                        <td className="imgcell" rowSpan={n}>
                                            <img src={resolveSwagImage(item.imagePath)} alt="" onError={(e) => { e.currentTarget.src = SWAG_IMAGE_PLACEHOLDER; }} />
                                        </td>
                                    )}
                                    {i === 0 && (
                                        <td className="itemcell" rowSpan={n}>{item.itemDescription || '—'}</td>
                                    )}
                                    <td>{s.sizeName || '—'}</td>
                                    <td className="num">{remaining}</td>
                                    <td className="bin">{bin || '—'}</td>
                                </tr>
                            );
                        });
                    })}
                </tbody>
            </table>
        </div>
    );
}

function ItemCard({ item, edits, binEdits, onEdit, onBinEdit, onGroupBin }) {
    // Group bin value: the common bin across sizes, or '' if they differ.
    const effBin = (s) => (s.sipSwagItemInventoryID in binEdits) ? binEdits[s.sipSwagItemInventoryID] : (s.bin ?? '');
    const binVals = item.sizes.map(effBin);
    const groupBin = binVals.every((v) => v === binVals[0]) ? (binVals[0] || '') : '';
    return (
        <div style={styles.itemCard}>
            <div style={styles.itemHeader}>
                <img
                    src={resolveSwagImage(item.imagePath)}
                    alt={item.itemDescription}
                    style={styles.itemImage}
                    onError={(e) => { e.currentTarget.src = SWAG_IMAGE_PLACEHOLDER; }}
                />
                <div style={styles.itemHeaderText}>
                    <div style={styles.itemTitle}>{item.itemDescription || '—'}</div>
                    <div style={styles.itemMeta}>{item.pointGroup ?? 0} pts</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>Bin (set all):</label>
                        <input
                            type="text"
                            value={groupBin}
                            maxLength={3}
                            placeholder={item.sizes.length > 1 ? 'varies' : ''}
                            onChange={(e) => onGroupBin(item, e.target.value)}
                            style={{ ...styles.cellInput, width: 52, textAlign: 'center', textTransform: 'uppercase' }}
                            title="Sets the Bin for every size of this item"
                        />
                    </div>
                </div>
            </div>
            <table style={styles.sizeTable}>
                <thead style={styles.sizeHead}>
                    <tr>
                        <th style={styles.th}>Size</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>Bin</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Ordered</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Remaining</th>
                    </tr>
                </thead>
                <tbody>
                    {item.sizes.map((s) => {
                        const id = s.sipSwagItemInventoryID;
                        const edited = id in edits;
                        const value = edited ? edits[id] : (s.remainingQuantity ?? 0);
                        const binEdited = id in binEdits && binEdits[id] !== (s.bin ?? '');
                        const binValue = (id in binEdits) ? binEdits[id] : (s.bin ?? '');
                        return (
                            <tr key={id} style={{ background: (edited || binEdited) ? '#fefce8' : 'transparent', borderBottom: '1px solid #f3f4f6' }}>
                                <td style={styles.td}>{s.sizeName || '—'}</td>
                                <td style={{ ...styles.td, textAlign: 'center' }}>
                                    <input
                                        type="text"
                                        value={binValue}
                                        maxLength={3}
                                        onChange={(e) => onBinEdit(id, e.target.value)}
                                        style={{ ...styles.cellInput, width: 48, textAlign: 'center', textTransform: 'uppercase' }}
                                    />
                                </td>
                                <td style={{ ...styles.td, textAlign: 'right', color: '#6b7280' }}>{s.totalSelectedQuantity ?? 0}</td>
                                <td style={{ ...styles.td, textAlign: 'right' }}>
                                    <input
                                        type="number" min="0"
                                        value={value}
                                        onChange={(e) => onEdit(id, e.target.value)}
                                        style={styles.cellInput}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function Banner({ kind, children }) {
    const color = kind === 'error' ? '#dc2626' : kind === 'success' ? '#15803d' : '#374151';
    const bg = kind === 'error' ? '#fef2f2' : kind === 'success' ? '#f0fdf4' : '#f3f4f6';
    return <div style={{ margin: '12px 0', padding: '8px 12px', borderRadius: 4, background: bg, color, fontSize: 13 }}>{children}</div>;
}

const btn = (bg, disabled) => ({
    background: bg, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 4,
    fontWeight: 600, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1
});

// Print sheet is hidden on screen, shown (and the editing UI hidden) on print.
// Larger data font, big photo column with no in-group dividers (rowspan).
const PRINT_CSS = `
.inv-print { display: none; }
@media print {
    .inv-noprint { display: none !important; }
    .inv-print { display: block !important; }
    .inv-print table { width: 100%; border-collapse: collapse; }
    .inv-print th, .inv-print td { border: 1px solid #94a3b8; padding: 6px 10px; font-size: 20px; text-align: left; vertical-align: middle; }
    .inv-print th { background: #e2e8f0; font-size: 14px; }
    .inv-print .imgcol { width: 130px; text-align: center; }
    .inv-print .imgcell { text-align: center; padding: 4px; }
    .inv-print .imgcell img { width: 120px; height: 120px; object-fit: cover; }
    .inv-print .itemcell { font-weight: 700; }
    .inv-print .num { text-align: right; }
    .inv-print .bin { font-weight: 700; }
    .inv-print tr { page-break-inside: avoid; }
    @page { margin: 12mm; }
}`;

const styles = {
    card: { background: 'white', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' },
    cardTitle: { marginTop: 0, marginBottom: 6, fontSize: 18, fontWeight: 800, color: '#111827' },
    muted: { color: '#6b7280', fontSize: 13 },
    eyebrow: { fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase' },
    title: { fontSize: 20, fontWeight: 800, color: '#111827', marginTop: 2 },
    headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 },
    actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    bulkRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    bulkLabel: { fontSize: 12, fontWeight: 700, color: '#374151' },
    input: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12, marginTop: 12 },
    itemCard: { background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' },
    itemHeader: { display: 'flex', gap: 12, padding: 12, alignItems: 'center', borderBottom: '1px solid #f3f4f6' },
    itemImage: { width: 64, height: 64, objectFit: 'cover', borderRadius: 6, background: '#f3f4f6', border: '1px solid #e5e7eb' },
    itemHeaderText: { minWidth: 0 },
    itemTitle: { fontSize: 14, fontWeight: 700, color: '#111827', wordBreak: 'break-word' },
    itemMeta: { fontSize: 12, color: '#6b7280', marginTop: 2 },
    sizeTable: { width: '100%', borderCollapse: 'collapse' },
    sizeHead: { background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    th: { textAlign: 'left', padding: '8px 12px', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' },
    td: { padding: '8px 12px', fontSize: 13, color: '#111827' },
    cellInput: { width: 64, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, textAlign: 'right', fontSize: 13 },
};

// Scoped picker CSS — buttons forced to width:auto so a global button rule
// can't blow them full-width (which was squeezing the text column).
const PICKER_CSS = `
.invpick-overlay { position:fixed; inset:0; background:rgba(0,0,0,.4); display:flex; align-items:center; justify-content:center; z-index:60; padding:16px; }
.invpick-modal { background:#fff; border-radius:10px; padding:18px; max-width:560px; width:100%; max-height:80vh; display:flex; flex-direction:column; box-shadow:0 10px 40px rgba(0,0,0,.25); }
.invpick-head { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:6px; }
.invpick-title { font-size:16px; font-weight:800; color:#111827; }
.invpick-note { color:#6b7280; font-size:13px; line-height:1.4; margin:4px 0 8px; }
.invpick-msg { padding:8px 10px; border-radius:6px; font-size:13px; margin:0 0 8px; }
.invpick-search { width:100%; padding:8px 10px; border:1px solid #d1d5db; border-radius:6px; font-size:13px; box-sizing:border-box; margin-bottom:8px; }
.invpick-list { overflow-y:auto; }
.invpick-muted { color:#6b7280; font-size:13px; padding:8px 0; }
.invpick-row { display:flex; align-items:center; gap:10px; padding:8px 4px; border-bottom:1px solid #f3f4f6; }
.invpick-img { width:40px; height:40px; flex:0 0 40px; object-fit:cover; border-radius:6px; border:1px solid #e5e7eb; background:#f9fafb; }
.invpick-text { flex:1 1 auto; min-width:0; }
.invpick-name { font-size:13px; font-weight:700; color:#111827; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.invpick-sub { font-size:12px; color:#6b7280; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.invpick-close, .invpick-add { flex:0 0 auto; width:auto; border:none; border-radius:6px; padding:7px 16px; font-size:13px; font-weight:700; cursor:pointer; white-space:nowrap; color:#fff; }
.invpick-close { background:#6b7280; }
.invpick-add { background:#2563eb; }
.invpick-add:disabled { background:#9ca3af; cursor:default; }
`;

export default InventoryManager;
